import Vue from 'vue';

Vue.component('booking-widget', {
    props: {
        fieldKey: {
            type: String,
            required: true
        },
        item: {
            type: Object,
            required: true
        },
        form: {
            type: Object,
            required: true
        },
        counterLabel: {
            type: String,
            default: ''
        },
        unlimitedLabel: {
            type: String,
            default: ''
        },
        noneAvailableLabel: {
            type: String,
            default: ''
        },
        maxSelection: {
            type: [Number, String],
            default: null
        },
        minSelection: {
            type: Number,
            default: null
        },
        capacityUsage: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            selectedOptions: []
        }
    },
    computed: {
        parsedOptions() {
            if (!Array.isArray(this.item.options)) {
                return [];
            }

            return this.item.options
                .filter(opt => opt?.value?.trim())
                .map(option => {
                    const label = option.value.trim();
                    const capacity = option.capacity;

                    if (!capacity || capacity === '') {
                        return { label, capacity: null, unlimited: false };
                    }

                    if (typeof capacity === 'string' && capacity.toLowerCase() === 'unlimited') {
                        return { label, capacity: null, unlimited: true };
                    }

                    const numCapacity = parseInt(capacity, 10);
                    return {
                        label,
                        capacity: !isNaN(numCapacity) && numCapacity > 0 ? numCapacity : null,
                        unlimited: false
                    };
                });
        },
        optionsWithAvailability() {
            return this.parsedOptions.map(option => {
                const optionCapacityData = this.capacityUsage[option.label] || {};
                const capacityWasSet = option.capacity !== null || option.unlimited;
                const isUnlimited = capacityWasSet && (optionCapacityData.unlimited === true || option.unlimited === true);

                let available = null;
                if (isUnlimited) {
                    available = null;
                } else if (optionCapacityData.available != null) {
                    available = this.normalizeNumber(optionCapacityData.available);
                } else if (capacityWasSet && !isUnlimited) {
                    available = option.capacity || 0;
                }

                const isAvailable = capacityWasSet && (
                    isUnlimited ||
                    (available != null && available > 0)
                );

                return {
                    ...option,
                    available,
                    isSelected: this.selectedOptions.includes(option.label),
                    isAvailable,
                    capacityWasSet,
                    unlimited: isUnlimited
                };
            });
        },
        canSelectMore() {
            if (!this.maxSelection || this.maxSelection === 'null' || this.maxSelection === '') {
                return true;
            }
            const max = parseInt(this.maxSelection, 10);
            return isNaN(max) || max <= 0 || this.selectedOptions.length < max;
        }
    },
    watch: {
        optionsWithAvailability: {
            handler(options) {
                const unavailableSelected = options
                    .filter(opt => opt.isSelected && !opt.isAvailable)
                    .map(opt => opt.label);

                if (unavailableSelected.length > 0) {
                    this.selectedOptions = this.selectedOptions.filter(label => !unavailableSelected.includes(label));
                }
            },
            deep: true,
            immediate: true
        },
        selectedOptions(newVal) {
            this.$set(this.form, this.fieldKey, newVal);
            this.$emit('change');
        }
    },
    mounted() {
        if (!Array.isArray(this.form[this.fieldKey])) {
            this.$set(this.form, this.fieldKey, []);
        }
        this.selectedOptions = this.form[this.fieldKey] || [];
    },
    methods: {
        normalizeNumber(value) {
            const num = parseInt(value, 10);
            return isNaN(num) ? 0 : num;
        },
        toggleOption(option) {
            const index = this.selectedOptions.indexOf(option.label);
            if (index > -1) {
                this.selectedOptions.splice(index, 1);
                return;
            }

            if (!this.canSelectMore) {
                return;
            }

            const optionData = this.optionsWithAvailability.find(opt => opt.label === option.label);
            if (optionData?.isAvailable) {
                this.selectedOptions.push(option.label);
            }
        },
        getAvailabilityText(option) {
            if (!option.capacityWasSet) {
                return '';
            }

            if (option.unlimited || option.available == null) {
                return this.unlimitedLabel || '';
            }

            if (option.available > 0) {
                return this.counterLabel ? `${option.available} ${this.counterLabel}` : `${option.available}`;
            }

            return this.noneAvailableLabel || '';
        }
    },
    template: `
        <div class="booking-widget-container">
            <div class="booking-options">
                <div
                    v-for="option in optionsWithAvailability"
                    :key="option.label"
                    class="booking-option form-check"
                    :class="{
                        'booking-option--selected': option.isSelected,
                        'booking-option--unavailable': !option.isAvailable
                    }"
                >
                    <div class="d-flex justify-content-between align-items-center w-100" :style="{
                        opacity: !option.isAvailable ? '0.5' : '1'
                    }">
                        <span class="d-flex align-items-center">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                :id="fieldKey + '_' + option.label.replace(/[^a-zA-Z0-9]/g, '_')"
                                :checked="option.isSelected"
                                :disabled="!option.isAvailable || (!option.isSelected && !canSelectMore)"
                                @change="toggleOption(option)"
                            />
                            <label
                                class="form-check-label"
                                :for="fieldKey + '_' + option.label.replace(/[^a-zA-Z0-9]/g, '_')"
                                :style="{ textDecoration: !option.isAvailable ? 'line-through' : 'none' }"
                                v-html="option.label"
                            ></label>
                        </span>
                        <span class="ms-3 fs-sm" :class="{
                            'color-neutrals-4': option.available !== 0 && option.available !== null,
                            'color-danger': option.available === 0 && !option.unlimited,
                            'color-success': option.unlimited
                        }">
                            {{ getAvailabilityText(option) }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    `
});
